import { createSelector, createSlice } from "@reduxjs/toolkit";
import { generateClient } from 'aws-amplify/api';
import { createGroup, deleteGroup, setUserGroups, updateGroup } from "../graphql/mutations";
import { groupByLocationId, searchGroups } from "../graphql/customQueries";
import { popPreviousToken, pushPreviousToken, reset, resetPagination } from "./globalActions";
import { getGroup, listGroups } from "../graphql/queries";

const client = generateClient();

const moduleName = "group";

const initialState = {
    loading: false,
    items: [],
    nextToken: null,
    previousTokens: [],
};

const groupSlice = createSlice({
    name: moduleName,
    initialState,
    extraReducers: (builder) => {
        builder.addCase(reset, () => initialState);
        builder.addCase(resetPagination, (state) => {
            state.nextToken = null;
            state.previousTokens = [];
        });
        builder.addCase(popPreviousToken, (state) => {
            state.previousTokens.pop();
        });
        builder.addCase(pushPreviousToken, (state, action) => {
            state.previousTokens.push(action.payload);
        });
    },
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setItems: (state, action) => {
            state.items = action.payload.items;
            state.nextToken = action.payload.nextToken;
        },
        updateItem: (state, action) => {
            const idx = state.items.findIndex(item => item.id === action.payload.id);
            if (idx < 0) {
                state.items.push(action.payload);
            } else {
                state.items[idx] = action.payload;
            }
        },
        deleteItem: (state, action) => {
            const idx = state.items.findIndex(item => item.id === action.payload);
            if (idx >= 0) {
                state.items = [...state.items.slice(0, idx), ...state.items.slice(idx + 1)]
            }
        }
    },
});

export const { setLoading, setItems, updateItem, deleteItem } = groupSlice.actions;
export default groupSlice.reducer;

export const selectState = (state) => state[moduleName];
export const selectGroupsLoading = createSelector(selectState, state => state.loading);
export const selectPagination = createSelector(selectState, (state) => ({ nextToken: state.nextToken, previousTokens: state.previousTokens }));
export const selectGroups = createSelector(selectState, (state) => state.items);


export const createNewGroup = (input) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: createGroup,
            variables: {
                input: {
                    ...input,
                    createdAt: new Date(),
                }
            },
        });
        dispatch(updateItem(data.createGroup));
        return data.createGroup;
    } catch (ex) {
        console.log("failed to create group", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const modifyGroup = ({ members, location, __typename, ...input }) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: updateGroup,
            variables: {
                input
            },
        });
        dispatch(updateItem(data.updateGroup));
        return data.updateGroup;
    } catch (ex) {
        console.log("failed to create group", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchGroup = (id, locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: getGroup,
            variables: {
                id,
                locationId,
            },
        });
        dispatch(updateItem(data.getGroup));
        return data.getGroup;
    } catch (ex) {
        console.log("failed to get group", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchGroups = (ids) => async (dispatch) => {
    const { data } = await client.graphql({
        query: listGroups,
        variables: {
            filter: {
                or: ids.map(id => ({ id: { eq: id } }))
            }
        },
    });
    return data.listGroups.items;
}

export const findGroups = (search, locationId, nextToken, storeNextToken = true, includeDivisions = false) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        if (storeNextToken) dispatch(pushPreviousToken(nextToken));
        else dispatch(popPreviousToken());

        let filter = {
            and: [
                { name: { wildcard: `*${search}*` }, },
                { locationId: { eq: locationId } },
            ]
        }

        if (!includeDivisions) filter.and.push({ division: { eq: includeDivisions } })

        const { data } = await client.graphql({
            query: searchGroups,
            variables: {
                nextToken,
                filter,
                limit: 12,
                sort: [{ field: 'name', direction: 'asc' }]
            },
        });
        dispatch(setItems(data.searchGroups));
        return data.searchGroups;
    } finally {
        dispatch(setLoading(false));
    }
};

export const fetchListGroups = (locationId, nextToken, storeNextToken = true, includeDivisions = false) => async (dispatch) => {
    try {
        dispatch(setLoading(true));

        if (storeNextToken) dispatch(pushPreviousToken(nextToken));
        else dispatch(popPreviousToken());
        let filter = {}
        if (!includeDivisions) filter.division = { eq: includeDivisions }
        const { data } = await client.graphql({
            query: groupByLocationId,
            variables: {
                locationId,
                limit: 12,
                nextToken,
                filter,
            },
        });
        dispatch(setItems(data.groupByLocationId));
        return data.groupByLocationId.items;
    } catch (ex) {
        console.log("failed to get crewMembers", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const removeGroup = (id, locationId) => async (dispatch) => {
    try {
        dispatch(setLoading(true));
        const { data } = await client.graphql({
            query: deleteGroup,
            variables: {
                input: { id, locationId },
            },
        });
        dispatch(deleteItem(id));
        return data.deleteGroup;
    } catch (ex) {
        console.log("failed to delete group", ex);
    } finally {
        dispatch(setLoading(false));
    }
};

export const assignUserToGroups = (crewMemberId, locationId, groupIds, division) => async (dispatch) => {
    const { data } = await client.graphql({
        query: setUserGroups,
        variables: {
            input: {
                crewMemberId,
                locationId,
                groupIds,
                division
            }
        }
    })
    if (!data.setUserGroups?.status) throw new Error(data.setUserGroups?.message)
}